import { Component, OnInit, Input, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ObjectId } from 'bson';
import { filter, Subscription, take } from 'rxjs';

import {
  CategorySourceTypes,
  ILegacyList,
  ItemStates,
  TableCellData,
} from '@site-mate/dashpivot-shared-library';

import { IDryList, TemplateListsService } from 'app/apps/template-lists.service';
import { ToastrService } from 'app/shared/service/toastr.service';

import { ITemplateTableCell } from '../template-table-cell.interface';

@Component({
  styleUrls: ['template-table-list-cell.component.scss'],
  templateUrl: 'template-table-list-cell.component.html',
})
export class TemplateTableListCellComponent implements ITemplateTableCell, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly templateListsService = inject(TemplateListsService);
  private readonly toastrService = inject(ToastrService);

  @Input() cellData: TableCellData;
  @Input() companyId: string;
  static identifierIndex = 0;
  readonly CategorySourceTypes = CategorySourceTypes;
  uniqueIdentifier: number;
  editingListItem: boolean;
  itemLabel = '';
  loading: boolean;
  availableLists = new Map<IDryList['_id'], IDryList>();
  unavailableLists = new Set<IDryList['_id']>();
  listReferenceDeleted = false;

  ngOnInit(): void {
    // eslint-disable-next-line no-plusplus
    this.uniqueIdentifier = TemplateTableListCellComponent.identifierIndex++;
    this.cellData.listSource = this.cellData.listSource || CategorySourceTypes.List;
    this.getAvailableLists();
    this.setupLoadingState();
    this.handleMissingList();
  }

  private getAvailableLists(): Subscription {
    return this.templateListsService.availableLists$.pipe(filter(Boolean), take(1)).subscribe((lists) => {
      lists.forEach((list) => this.availableLists.set(list._id, list));
    });
  }

  setupLoadingState(): Subscription {
    return this.templateListsService.listIsLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((listIsLoadingMap) => {
        this.loading = listIsLoadingMap.get(this.cellData.reference);
      });
  }

  private handleMissingList() {
    return this.templateListsService
      .getListData(this.cellData.reference)
      .pipe(
        filter((list) => Boolean(list) && this.isListUnavailable()),
        take(1),
      )
      .subscribe({
        next: (list) => {
          const { _id, name } = list;
          this.unavailableLists.add(this.cellData.reference);
          this.availableLists.set(_id, { _id, name });
          this.updateListIfWasDeleted(list);
        },
        error: (err) => {
          if (err.status === 401) {
            this.unavailableLists.add(this.cellData.reference);
          }
        },
      });
  }

  private updateListIfWasDeleted(list: ILegacyList) {
    if (list.state === ItemStates.Deleted) {
      this.availableLists.delete(list._id);
      this.listReferenceDeleted = true;
      this.cellData.reference = null;
      this.toastrService.errorByKey('listInTemplateIsDeleted', {
        toastClass: 'ngx-toastr toast-long-message',
      });
    }
  }

  private isListUnavailable() {
    return this.cellData.reference && !this.availableLists.has(this.cellData.reference);
  }

  onListSourceChange(sourceType: CategorySourceTypes) {
    if (sourceType === CategorySourceTypes.List) {
      delete this.cellData.manualListItems;
    } else if (sourceType === CategorySourceTypes.Manual) {
      this.cellData.reference = null;
      this.cellData.content = [];
    }
  }

  addListItem(event: HTMLInputElement) {
    if (event.value) {
      this.cellData.manualListItems = this.cellData.manualListItems || [];

      this.cellData.manualListItems.push({
        id: String(new ObjectId()),
        value: event.value,
        style: this.cellData.defaultColor,
      });
    } else {
      this.toastrService.warnByKey('inputTextBeforeAdd');
    }
    event.value = '';
    event.focus();
  }

  removeListItem(id: string) {
    this.cellData.manualListItems = this.cellData.manualListItems.filter((i) => i.id !== id);
  }

  onListChange() {
    this.listReferenceDeleted = false;
  }

  onEditListItem(item) {
    this.editingListItem = item;
  }
}
